import React, {useCallback} from 'react'

import {useSnackbar} from 'notistack'

import {
  CreateAccountMutationVariables,
  MeDocument,
  SetupProfileMutationVariables,
  SetupMenopauseProfileMutationVariables,
  SetupPregnancyPostpartumProfileMutationVariables,
  useCreateAccountMutation,
  useSetupProfileMutation,
  useSetupMenopauseProfileMutation,
  useSetupPregnancyPostpartumProfileMutation,
} from '@progyny/generated/graphql'
import {useErrorCodes} from '@progyny/hooks/useErrorCodes'
import {useMeStore} from '@progyny/store/meStore'

export default function useOnboardingMutations() {
  const [createAccountMutation, {loading: loadingCreate}] =
    useCreateAccountMutation()
  const [setupProfileMutation, {loading: loadingSetup}] =
    useSetupProfileMutation()
  const [setupMenopauseProfileMutation, {loading: loadingMenopauseSetup}] =
    useSetupMenopauseProfileMutation()
  const [
    setupPregnancyPostpartumProfileMutation,
    {loading: loadingPregnancySetup},
  ] = useSetupPregnancyPostpartumProfileMutation()
  const getErrorMessage = useErrorCodes()
  const {enqueueSnackbar} = useSnackbar()
  const isInConservativeCountry = useMeStore((s) => s.isInConservativeCountry)

  const persistCreateAccountData = useCallback(
    async (variables: CreateAccountMutationVariables) => {
      try {
        const result = await createAccountMutation({
          variables,
          refetchQueries: [{query: MeDocument}],
          awaitRefetchQueries: true,
        })

        if (!result.data?.createAccount?.success) {
          const backendMessage =
            result.data?.createAccount?.errors?.[0]?.messages?.[0]
          const errorCode =
            backendMessage ??
            result.data?.createAccount?.errorCode ??
            'unknown-error'

          enqueueSnackbar({
            message: getErrorMessage(errorCode),
            variant: 'error',
          })
          throw new Error(`createAccount mutation failed: ${errorCode}`)
        }
      } catch (e) {
        console.error(e)
        throw e
      }
    },
    [createAccountMutation, enqueueSnackbar, getErrorMessage]
  )

  const persistSetupInformation = useCallback(
    (
      variables: SetupProfileMutationVariables,
      onSuccess: React.Dispatch<void>
    ) => {
      if (
        isInConservativeCountry() &&
        variables.hasOwnProperty('birthAssignedSex')
      ) {
        variables.genderIdentity = variables.birthAssignedSex
      }

      setupProfileMutation({
        variables,
        refetchQueries: [{query: MeDocument}],
        awaitRefetchQueries: true,
      })
        .then((result) => {
          if (
            result.data?.setupProfile?.success === false &&
            result.data.setupProfile.errorCode
          ) {
            const message = getErrorMessage(result.data.setupProfile.errorCode)
            enqueueSnackbar({message, variant: 'error'})
          } else {
            onSuccess()
          }
        })
        .catch((err) => {
          console.error(err)
          enqueueSnackbar({
            message: getErrorMessage('unknown-error'),
            variant: 'error',
          })
        })
    },
    [
      enqueueSnackbar,
      getErrorMessage,
      setupProfileMutation,
      isInConservativeCountry,
    ]
  )

  const persistMenopauseProfileInformation = useCallback(
    (
      variables: SetupMenopauseProfileMutationVariables,
      onSuccess: React.Dispatch<void>
    ) => {
      setupMenopauseProfileMutation({
        variables,
        refetchQueries: [{query: MeDocument}],
        awaitRefetchQueries: true,
        fetchPolicy: 'network-only', // Force a network request
      })
        .then((result) => {
          if (
            result.data?.setupMenopauseProfile?.success === false &&
            result.data.setupMenopauseProfile.errorCode
          ) {
            const message = getErrorMessage(
              result.data.setupMenopauseProfile.errorCode
            )
            enqueueSnackbar({message, variant: 'error'})
          } else {
            onSuccess()
          }
        })
        .catch((err) => {
          console.error('setupMenopauseProfileMutation error:', err)
          enqueueSnackbar({
            message: getErrorMessage('unknown-error'),
            variant: 'error',
          })
        })
    },
    [enqueueSnackbar, getErrorMessage, setupMenopauseProfileMutation]
  )

  const persistPregnancyPostpartumProfileInformation = useCallback(
    (
      variables: SetupPregnancyPostpartumProfileMutationVariables,
      onSuccess: React.Dispatch<void>
    ) => {
      setupPregnancyPostpartumProfileMutation({
        variables,
        refetchQueries: [{query: MeDocument}],
        awaitRefetchQueries: true,
        fetchPolicy: 'network-only', // Force a network request
      })
        .then((result) => {
          if (
            result.data?.setupPregnancyPostpartumProfile?.success === false &&
            result.data.setupPregnancyPostpartumProfile.errorCode
          ) {
            const message = getErrorMessage(
              result.data.setupPregnancyPostpartumProfile.errorCode
            )
            enqueueSnackbar({message, variant: 'error'})
          } else {
            onSuccess()
          }
        })
        .catch((err) => {
          console.error('setupPregnancyPostpartumProfileMutation error:', err)
          enqueueSnackbar({
            message: getErrorMessage('unknown-error'),
            variant: 'error',
          })
        })
    },
    [enqueueSnackbar, getErrorMessage, setupPregnancyPostpartumProfileMutation]
  )

  return {
    updateLoading:
      loadingCreate ||
      loadingSetup ||
      loadingMenopauseSetup ||
      loadingPregnancySetup,
    persistCreateAccountData,
    persistSetupInformation,
    persistMenopauseProfileInformation,
    persistPregnancyPostpartumProfileInformation,
  }
}
